/*
Purpose: Requests UI docked in the right rail (below Filters) — no slide-over drawer.
*/

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { CALENDAR_VIEW } from '../constants';
import { INBOX_IDS, INBOX_TABS } from '../request-tabs';
import {
  DeferBinInbox,
  DeferRequestsInbox,
  DisapprovedRequestsInbox,
  OneOffRequestsInbox,
} from './RequestInboxes';

export { INBOX_TABS } from '../request-tabs';

/** Global lookback windows for all request tabs. */
export const REQUEST_RANGE_OPTIONS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
];

export const DEFAULT_REQUEST_RANGE_DAYS = 30;

/**
 * Keep rows whose session date is on/after (today − days). Future dates stay visible.
 * Uses `sDate`, with `ses_date` as fallback.
 */
export function filterRecordsByLookbackDays(records, days, now = Moment()) {
  if (!Array.isArray(records) || records.length === 0) {
    return [];
  }

  const cutoff = Moment(now).startOf('day').subtract(days, 'days');

  return records.filter((record) => {
    const raw = record.sDate || record.ses_date;
    if (!raw) {
      return false;
    }
    const date = Moment(raw, ['YYYY-MM-DD', Moment.ISO_8601], true);
    if (!date.isValid()) {
      return false;
    }
    return !date.startOf('day').isBefore(cutoff);
  });
}

/**
 * @param {Object} props
 * @param {string} props.inbox One of the request `CALENDAR_VIEW` values.
 * @param {Object} props.inboxes Loaded inbox record lists from `useCalendarData`.
 * @param {Function} props.onSelectInbox Switch inbox type without closing the panel.
 * @param {Function} props.onClose Clears the active inbox (filters stay visible above).
 */
export default function RequestsPanel({
  inbox,
  inboxes,
  busyKey,
  isBusy,
  selectedKeys,
  onToggleSelect,
  onStartAssign,
  onSelectSlot,
  onApproveDefer,
  onDisapproveDefer,
  onSettleDeferred,
  onVoidDeferment,
  onSelectInbox,
  onClose,
}) {
  const [rangeDays, setRangeDays] = useState(DEFAULT_REQUEST_RANGE_DAYS);

  const filteredInboxes = useMemo(
    () => ({
      deferRequests: filterRecordsByLookbackDays(inboxes.deferRequests, rangeDays),
      deferBin: filterRecordsByLookbackDays(inboxes.deferBin, rangeDays),
      oneOffRequests: filterRecordsByLookbackDays(inboxes.oneOffRequests, rangeDays),
      disapproved: filterRecordsByLookbackDays(inboxes.disapproved, rangeDays),
    }),
    [inboxes, rangeDays]
  );

  const filteredCounts = useMemo(
    () => ({
      deferRequests: filteredInboxes.deferRequests.length,
      deferBin: filteredInboxes.deferBin.length,
      oneOffRequests: filteredInboxes.oneOffRequests.length,
      disapproved: filteredInboxes.disapproved.length,
    }),
    [filteredInboxes]
  );

  const activeTab = INBOX_TABS.find((tab) => tab.id === inbox) || INBOX_TABS[0];
  const ActiveIcon = activeTab.icon;

  let records = [];
  let body = null;

  switch (inbox) {
    case CALENDAR_VIEW.DEFER_REQUESTS:
      records = filteredInboxes.deferRequests;
      body = (
        <DeferRequestsInbox
          embedded
          records={records}
          busyKey={busyKey}
          isBusy={isBusy}
          onBack={onClose}
          onApprove={onApproveDefer}
          onDisapprove={onDisapproveDefer}
        />
      );
      break;

    case CALENDAR_VIEW.DEFER_BIN:
      records = filteredInboxes.deferBin;
      body = (
        <DeferBinInbox
          embedded
          records={records}
          busyKey={busyKey}
          isBusy={isBusy}
          onBack={onClose}
          selectedKeys={selectedKeys}
          onToggleSelect={onToggleSelect}
          onStartAssign={onStartAssign}
          onSelectSlot={onSelectSlot}
          onForfeit={onSettleDeferred}
          onWriteOff={onSettleDeferred}
          onVoid={onVoidDeferment}
        />
      );
      break;

    case CALENDAR_VIEW.ONE_OFF_REQUESTS:
      records = filteredInboxes.oneOffRequests;
      body = (
        <OneOffRequestsInbox
          embedded
          records={records}
          isBusy={isBusy}
          onBack={onClose}
          selectedKeys={selectedKeys}
          onToggleSelect={onToggleSelect}
          onStartAssign={onStartAssign}
        />
      );
      break;

    case CALENDAR_VIEW.DISAPPROVED:
      records = filteredInboxes.disapproved;
      body = <DisapprovedRequestsInbox embedded records={records} onBack={onClose} />;
      break;

    default:
      return null;
  }

  return (
    <aside className="cv2-panel cv2-panel--requests" aria-label="Requests">
      <header className="cv2-req-panel__head">
        <div className="cv2-req-panel__top">
          <span className="cv2-panel__title">
            <InboxOutlinedIcon className="cv2-icon" />
            Requests
          </span>
          <button
            type="button"
            className="cv2-btn cv2-btn--icon"
            onClick={onClose}
            aria-label="Close requests"
          >
            <CloseIcon className="cv2-icon" />
          </button>
        </div>

        <div className="cv2-req-panel__sub">
          <ActiveIcon className="cv2-icon" style={{ fontSize: 14 }} />
          {activeTab.fullTitle}
          <span className="cv2-req-drawer__count-dot">·</span>
          {records.length} item{records.length === 1 ? '' : 's'}
          <span className="cv2-req-drawer__count-dot">·</span>
          Last {rangeDays} days
        </div>

        <div
          className="cv2-req-drawer__range"
          role="group"
          aria-label="Show requests from the last"
        >
          {REQUEST_RANGE_OPTIONS.map((option) => {
            const isActive = option.days === rangeDays;
            return (
              <button
                key={option.days}
                type="button"
                className={`cv2-req-drawer__range-btn${isActive ? ' is-active' : ''}`}
                aria-pressed={isActive}
                onClick={() => setRangeDays(option.days)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="cv2-req-drawer__tabs" role="tablist" aria-label="Request type">
          {INBOX_TABS.map((tab) => {
            const count = filteredCounts[tab.countKey] || 0;
            const isActive = tab.id === inbox;
            const TabIcon = tab.icon;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`cv2-req-drawer__tab${isActive ? ' is-active' : ''}${
                  count > 0 && !isActive ? ' has-items' : ''
                }`}
                onClick={() => onSelectInbox(tab.id)}
              >
                <TabIcon className="cv2-icon cv2-req-drawer__tab-icon" />
                <span className="cv2-req-drawer__tab-label">{tab.label}</span>
                <span
                  className={`cv2-req-drawer__tab-count${
                    count > 0 ? ' cv2-req-drawer__tab-count--alert' : ''
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      <div className="cv2-panel__body cv2-panel__body--requests">{body}</div>
    </aside>
  );
}

RequestsPanel.propTypes = {
  inbox: PropTypes.oneOf(INBOX_IDS).isRequired,
  inboxes: PropTypes.shape({
    deferRequests: PropTypes.array.isRequired,
    deferBin: PropTypes.array.isRequired,
    oneOffRequests: PropTypes.array.isRequired,
    disapproved: PropTypes.array.isRequired,
  }).isRequired,
  busyKey: PropTypes.string,
  isBusy: PropTypes.bool,
  selectedKeys: PropTypes.array,
  onToggleSelect: PropTypes.func.isRequired,
  onStartAssign: PropTypes.func.isRequired,
  onSelectSlot: PropTypes.func.isRequired,
  onApproveDefer: PropTypes.func.isRequired,
  onDisapproveDefer: PropTypes.func.isRequired,
  onSettleDeferred: PropTypes.func.isRequired,
  onVoidDeferment: PropTypes.func.isRequired,
  onSelectInbox: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

RequestsPanel.defaultProps = {
  busyKey: null,
  isBusy: false,
  selectedKeys: [],
};
